import { useMemo, useState } from "react";

import { autorizar, sumarAutoPersonal } from "../../services/vigilancia";
import {
  avisoDeError,
  claseAutorizado,
  esVigente,
  otraConPatente,
  patenteBusqueda,
  sugerirPorNombre,
  sugerirPorPatente,
} from "../../shared/autosAutorizados";
import { hoyAr } from "../../shared/fechaAr";
import Sugerencias from "./Sugerencias";

/* ── Agregar a la lista: UN solo lugar (Nico, ticket 441, 19/10) ────────────
 *
 * Antes había dos formularios: «Sumar un auto» (autos del personal, sin
 * vencimiento, lo usa la barrera) y «Autorizar» (gente de afuera, con motivo y
 * hasta cuándo vale, sólo quien autoriza). Ahora es uno, y lo único que se
 * elige es CÓMO VALE:
 *
 *   · FIJO: sin vencimiento. Con patente, queda como auto de la casa
 *     (`sumarAutoPersonal`): es lo que la barrera ya podía cargar, corregir y
 *     dar de baja. Sin patente —alguien que entra caminando— lo puede dejar
 *     fijo sólo quien autoriza, como hasta ahora con la fecha vacía.
 *   · TEMPORAL: hasta una fecha (`autorizar`, con el motivo). Sólo quien
 *     autoriza. «Hasta» arranca en HOY, como el «Válida hasta» de antes y
 *     como por WhatsApp.
 *
 * NINGUNA DE LAS DOS VIENE ELEGIDA (revisión del ticket 441). Si arrancaba en
 * «Temporal, hasta hoy», un auto de la casa cargado de apuro quedaba como una
 * autorización de afuera de un día, y después la barrera no podía volver a
 * sumarlo como fijo: esa patente la renueva quien autoriza. Y arrancar en
 * «Fijo» haría eterno lo que nadie miró, que es lo que se evitó al hacer
 * arrancar «Hasta» en hoy (Nico, 29/09 y 06/10). Así que «Agregar» no anda
 * hasta que se elige una, y una línea dice qué falta.
 *
 * Los permisos son los de antes: quien sólo registra en la barrera ve el
 * formulario sin la elección —agrega fijos con patente y nombre, que es lo
 * único que puede, así que para la barrera ya viene en «fijo»— y la base
 * rebota cualquier otra cosa igual.
 *
 * MIENTRAS SE ESCRIBE, el mismo autocompletar de «Registrar entrada»: la
 * patente trae a la persona y el nombre trae la patente, de los autos que ya
 * entraron y de la lista misma. Elegir uno que ya está vigente no lo carga dos
 * veces: lo muestra en la lista. Elegir uno vencido o dado de baja completa el
 * formulario para volver a autorizarlo. */

const vacio = (puedeAutorizar) => ({
  patente: "",
  nombre: "",
  detalle: "",
  // Quien autoriza elige fijo o temporal; la barrera sólo puede fijo.
  vale: puedeAutorizar ? "" : "fijo",
  hasta: hoyAr(),
});

const FALTA_VALE = "Elegí cómo vale: fijo o temporal.";

const NOTA_CLASE = {
  fijo: " · ya está en la lista",
  temporal: " · ya está en la lista",
  vencido: " · vencido",
  baja: " · dado de baja",
};

const AgregarAutorizado = ({
  filas = [],
  recientes = [],
  sectores = [],
  puedeAutorizar = false,
  onAgregado,
  onCancelar,
  onMostrar,
}) => {
  const [f, setF] = useState(() => vacio(puedeAutorizar));
  const [activo, setActivo] = useState(null); // "patente" | "nombre"
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState("");

  const hoy = hoyAr();
  const vigentes = useMemo(
    () => filas.filter((a) => esVigente(claseAutorizado(a, hoy))),
    [filas, hoy],
  );
  const fuentes = useMemo(() => ({ autorizados: filas, recientes }), [filas, recientes]);
  const sugerencias = useMemo(() => {
    if (activo === "patente") return sugerirPorPatente(f.patente, fuentes);
    if (activo === "nombre") return sugerirPorNombre(f.nombre, fuentes);
    return [];
  }, [activo, f.patente, f.nombre, fuentes]);

  const poner = (c) => setF((v) => ({ ...v, ...c }));
  const temporal = puedeAutorizar && f.vale === "temporal";
  const faltaVale = puedeAutorizar && !f.vale;

  const nota = (s) =>
    s.tipo === "autorizado" ? NOTA_CLASE[claseAutorizado(s.autorizacion, hoy)] || "" : "";

  const elegir = (s) => {
    setActivo(null);
    setError(null);
    setAviso("");
    const clase = s.tipo === "autorizado" ? claseAutorizado(s.autorizacion, hoy) : null;
    if (esVigente(clase)) {
      setAviso(
        `Ya está en la lista: ${s.nombre || "sin nombre"}${s.patente ? ` (${s.patente})` : ""}.`,
      );
      onMostrar?.(s.autorizacion);
      return;
    }
    poner({ patente: s.patente || "", nombre: s.nombre || "", detalle: s.detalle || "" });
  };

  const agregar = async () => {
    setError(null);
    setAviso("");
    const p = patenteBusqueda(f.patente);
    const n = f.nombre.trim();
    if (faltaVale) return setError(FALTA_VALE);
    if (temporal) {
      if (!p && !n) return setError("Poné al menos el nombre o la patente.");
      if (!f.hasta) return setError("Poné hasta qué día vale.");
      // Una fecha ya pasada no autoriza a nadie: se guardaría y no aparecería
      // nunca en el portón.
      if (f.hasta < hoy) return setError("Esa fecha ya pasó: la autorización no habilitaría a nadie.");
    } else if (!n || (!p && !puedeAutorizar)) {
      return setError(puedeAutorizar && !p ? "Poné el nombre." : "Poné la patente y el nombre.");
    }
    // Antes de ir a la base, contra la lista que ya está en pantalla: si esa
    // patente ya está vigente, se dice de quién y no se carga otra vez.
    const otra = otraConPatente(vigentes, p);
    if (otra) {
      setError(`Esa patente ya está en la lista${otra.nombre ? ` (${otra.nombre})` : ""}.`);
      onMostrar?.(otra);
      return;
    }
    setGuardando(true);
    try {
      if (temporal) {
        await autorizar({ nombre: n, patente: p, motivo: f.detalle, vence: f.hasta });
      } else if (p) {
        const r = await sumarAutoPersonal({ patente: p, nombre: n, sector: f.detalle });
        if (r.yaEstaba) {
          setAviso(`Esa patente ya estaba en la lista${r.nombre ? ` (${r.nombre})` : ""}.`);
          setF(vacio(puedeAutorizar));
          onMostrar?.({ patente: p, nombre: r.nombre });
          return;
        }
      } else {
        await autorizar({ nombre: n, patente: "", motivo: f.detalle, vence: null });
      }
      setF(vacio(puedeAutorizar));
      onAgregado?.(`Listo: ${n || p} quedó en la lista.`);
    } catch (e) {
      setError(avisoDeError(e, "No se pudo agregar"));
    } finally {
      setGuardando(false);
    }
  };

  const listaPatente = activo === "patente" && sugerencias.length > 0;
  const listaNombre = activo === "nombre" && sugerencias.length > 0;

  return (
    <div className="vig-panel" role="group" aria-label="Agregar a la lista">
      <div className="vig-form">
        <div className="vig-campo">
          <label htmlFor="vig-ag-patente">Patente</label>
          <input
            id="vig-ag-patente"
            value={f.patente}
            onChange={(e) => {
              poner({ patente: e.target.value.toUpperCase() });
              setActivo("patente");
            }}
            placeholder="AB123CD"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="characters"
            spellCheck={false}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={listaPatente}
            aria-controls="vig-ag-sug-patente"
          />
          {listaPatente && (
            <Sugerencias id="vig-ag-sug-patente" items={sugerencias} onElegir={elegir} nota={nota} />
          )}
        </div>
        <div className="vig-campo">
          <label htmlFor="vig-ag-nombre">Nombre</label>
          <input
            id="vig-ag-nombre"
            value={f.nombre}
            onChange={(e) => {
              poner({ nombre: e.target.value });
              setActivo("nombre");
            }}
            placeholder="Apellido, nombre"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={listaNombre}
            aria-controls="vig-ag-sug-nombre"
          />
          {listaNombre && (
            <Sugerencias id="vig-ag-sug-nombre" items={sugerencias} onElegir={elegir} nota={nota} />
          )}
        </div>
        <div className="vig-campo">
          <label htmlFor="vig-ag-detalle">Sector / motivo</label>
          <input
            id="vig-ag-detalle"
            value={f.detalle}
            onChange={(e) => poner({ detalle: e.target.value })}
            placeholder="Taller, mantenimiento, a qué viene…"
            autoComplete="off"
            list="vig-ag-sectores"
          />
          <datalist id="vig-ag-sectores">
            {sectores.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </div>
        {puedeAutorizar && (
          <div className="vig-campo">
            <span id="vig-ag-vale">Vale</span>
            <div className="tk-filtros" role="group" aria-labelledby="vig-ag-vale">
              {[
                ["fijo", "Fijo"],
                ["temporal", "Temporal"],
              ].map(([id, texto]) => (
                <button
                  key={id}
                  type="button"
                  className={`tk-chip${f.vale === id ? " activo" : ""}`}
                  aria-pressed={f.vale === id}
                  onClick={() => poner({ vale: id })}
                >
                  {texto}
                </button>
              ))}
            </div>
          </div>
        )}
        {temporal && (
          <div className="vig-campo">
            <label htmlFor="vig-ag-hasta">Hasta</label>
            <input
              id="vig-ag-hasta"
              type="date"
              value={f.hasta}
              min={hoy}
              onChange={(e) => poner({ hasta: e.target.value })}
            />
          </div>
        )}
      </div>
      {error && <p className="panel-aviso">{error}</p>}
      {aviso && (
        <p className="panel-aviso suave" role="status">
          {aviso}
        </p>
      )}
      {faltaVale && (
        <p className="docs-pie" id="vig-ag-falta-vale">
          {FALTA_VALE}
        </p>
      )}
      <div className="vig-acciones">
        <button type="button" className="btn btn--ghost" onClick={onCancelar}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn"
          disabled={guardando || faltaVale}
          aria-describedby={faltaVale ? "vig-ag-falta-vale" : undefined}
          onClick={agregar}
        >
          {guardando ? "Guardando…" : "Agregar"}
        </button>
      </div>
    </div>
  );
};

export default AgregarAutorizado;
